const STORAGE_KEY = 'neona_cookie_consent'

export type ConsentValue = 'accepted' | 'declined'

const CONSENT_EVENT = 'neona:cookie-consent'
let volatileConsent: ConsentValue | null = null

function parse(value: string | null): ConsentValue | null {
  return value === 'accepted' || value === 'declined' ? value : null
}

// localStorage 는 프라이빗 모드·쿠키 차단·iframe 샌드박스에서 접근 자체가 던진다.
// 쓰기 실패 시에도 사용자가 방금 선택한 값은 현재 문서 수명 동안 유지한다.
function read(): string | null {
  if (volatileConsent !== null) return volatileConsent
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function getConsent(): ConsentValue | null {
  return parse(read())
}

export function setConsent(value: ConsentValue): void {
  volatileConsent = value
  try {
    localStorage.setItem(STORAGE_KEY, value)
    volatileConsent = null
  } catch {
    // quota 오류에서는 remove가 가능한 경우가 많다. stale 반대 선택이 다음 로드에서
    // 되살아나지 않도록 무효화하고 현재 문서에서는 volatileConsent를 유지한다.
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* storage 전체 접근 불가 */ }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }))
  }
}

export type ConsentChangeSource = 'local' | 'storage'

export function subscribeConsent(
  listener: (source: ConsentChangeSource, value: ConsentValue | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined

  const onLocal = (event: Event) => listener(
    'local',
    parse((event as CustomEvent<unknown>).detail as string | null),
  )
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      volatileConsent = null
      listener('storage', event.key === null ? null : parse(event.newValue))
    }
  }
  window.addEventListener(CONSENT_EVENT, onLocal)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CONSENT_EVENT, onLocal)
    window.removeEventListener('storage', onStorage)
  }
}

export function hasDecided(): boolean {
  return parse(read()) !== null
}
